import React, { useState } from 'react';
import { Plus, Minus } from 'lucide-react';

interface FAQProps {
  language: string;
}

const FAQ: React.FC<FAQProps> = ({ language }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const content = {
    en: {
      title: "Frequently Asked Questions",
      subtitle: "Find answers to common questions about our treatments and services",
      items: [
        {
          question: "How do I book a treatment?",
          answer: "You can easily book online through our website by selecting your desired treatment and time. Once payment is confirmed, your spot is secured. Need help? We're just a message away."
        },
        {
          question: "What is your cancellation policy?",
          answer: "We kindly ask for cancellations at least 24 hours in advance. Late cancellations may result in a 20% fee of the treatment price. Thank you for valuing our time and yours."
        },
        {
          question: "Is it safe if I have a medical condition?",
          answer: "Yes. Our team includes a licensed doctor with emergency medical experience. You'll receive a free, tailored consultation to ensure all treatments are safe and suitable for you."
        },
        {
          question: "How long do the results last?",
          answer: "Botox results last about 3–4 months, fillers up to 6–12 months, and facials provide instant glow with cumulative benefits. We'll guide you with a personal plan during your visit."
        },
        {
          question: "Can I get a refund for unused treatments?",
          answer: "Yes. If you've purchased a package and haven't used all sessions, you can request a refund. Used treatments will be recalculated at full price if they were part of a discounted offer."
        }
      ]
    },
    da: {
      title: "Ofte Stillede Spørgsmål",
      subtitle: "Find svar på almindelige spørgsmål om vores behandlinger og services",
      items: [
        {
          question: "Hvordan booker jeg en behandling?",
          answer: "Du kan nemt booke online via vores hjemmeside ved at vælge den ønskede behandling og tid. Når betalingen er bekræftet, er din tid reserveret. Brug for hjælp? Vi er kun en besked væk."
        },
        {
          question: "Hvad er jeres afbestillingspolitik?",
          answer: "Vi beder venligst om, at afbestillinger sker mindst 24 timer før. Sene afbestillinger kan medføre et gebyr på 20 % af behandlingens pris. Tak for at respektere vores og din tid."
        },
        {
          question: "Er det sikkert, hvis jeg har en medicinsk tilstand?",
          answer: "Ja. Vores team inkluderer en autoriseret læge med akutmedicinsk erfaring. Du får en gratis, skræddersyet konsultation for at sikre, at alle behandlinger er sikre og passende for dig."
        },
        {
          question: "Hvor længe holder resultaterne?",
          answer: "Botox holder i ca. 3–4 måneder, fillers op til 6–12 måneder, og ansigtsbehandlinger giver øjeblikkelig glød med akkumulerede fordele. Vi vejleder dig med en personlig plan under dit besøg."
        },
        {
          question: "Kan jeg få refusion for ubrugte behandlinger?",
          answer: "Ja. Hvis du har købt en pakke og ikke har brugt alle sessioner, kan du anmode om refusion. Brugte behandlinger vil blive genberegnet til fuld pris, hvis de var en del af et rabattilbud."
        }
      ]
    }
  };

  return (
    <section className="py-20 relative overflow-hidden bg-[#1A1A1A]" id="faq">
      {/* Decorative bottom border */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#A68B7C]/30 to-transparent" />
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-40 h-[2px] bg-gradient-to-r from-transparent via-[#A68B7C]/50 to-transparent" />
      <div className="absolute bottom-[1px] left-1/2 -translate-x-1/2 w-20 h-[1px] bg-gradient-to-r from-transparent via-[#A68B7C]/30 to-transparent" />

      {/* Ambient Background Effects */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(166,139,124,0.15),transparent)]" />
      <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url("data:image/png;base64,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")' }} />
      
      <div className="container mx-auto px-4 relative">
        <div className="text-center mb-16">
          <h2 
            className="text-3xl md:text-4xl font-serif font-bold mb-4 text-white"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {content[language as keyof typeof content].title}
          </h2>
          <p className="text-lg text-gray-400 max-w-3xl mx-auto">
            {content[language as keyof typeof content].subtitle}
          </p>
        </div>

        <div className="max-w-3xl mx-auto space-y-4">
          {content[language as keyof typeof content].items.map((item, index) => (
            <div
              key={index}
              className="group relative backdrop-blur-md rounded-2xl overflow-hidden transition-all duration-500 bg-gradient-to-r from-[#2A2A2A]/90 to-[#2A2A2A]/80 hover:from-[#2A2A2A] hover:to-[#2A2A2A]/90 border border-[#A68B7C]/20 shadow-[0_4px_20px_rgb(0,0,0,0.2)] hover:shadow-[0_8px_30px_rgb(166,139,124,0.1)]"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full text-left p-6 flex items-center justify-between gap-4"
              >
                <span className="text-lg font-serif font-medium text-white/90 transition-colors duration-300 group-hover:text-[#A68B7C]">
                  {item.question}
                </span>
                {openIndex === index ? (
                  <Minus className="w-5 h-5 text-[#A68B7C] flex-shrink-0 transition-transform duration-300 group-hover:scale-110" />
                ) : (
                  <Plus className="w-5 h-5 text-[#A68B7C] flex-shrink-0 transition-transform duration-300 group-hover:scale-110" />
                )}
              </button>

              <div
                className={`grid transition-all duration-300 ease-in-out ${
                  openIndex === index ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                }`}
              >
                <div className="overflow-hidden">
                  <p className="p-6 pt-0 text-gray-400 leading-relaxed">
                    {item.answer}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQ;